import styled from '@emotion/styled';
import { tokens, Alert, Loader } from '@hotjar/hotsauce';
import { useLocalStorageJSONValue } from '@hotjar/webapp/hooks/useLocalStorageValue';
import { useCallback, useMemo } from 'react';
import { ExportResults } from '@hotjar/feedback/components/common/ExportResults';
import { useCurrentSite } from '@hotjar/site';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { QuestionTypes } from '../../../../domain/Surveys/Questions';
import { ForwardSurveyResults } from '../../SurveyView/SurveyResultsView/ForwardSurveyResults';
import { useExportResponses } from '../../hooks/useExportResponses';
import { useSelectedQuestion } from '../../SurveyView/SurveyResultsView/useSelectedQuestion';
import { ResultsQuestionSelector } from '../../SurveyView/SurveyResultsView/ResultsQuestionSelector';

export interface ResultsControlsProps {
  totalAggregatedResults: number | undefined;
  surveyId: number;
}

const ALERT_WIDTH = 600;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  grid-gap: ${tokens.spacer.x0pt5};
  padding: ${tokens.spacer.x0} 0;
  align-self: flex-end;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${tokens.spacer.x4} 0 0 0;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${tokens.spacer.x5};
`;

const StyledAlert = styled(Alert)`
  width: ${ALERT_WIDTH}px;
  margin: 0 auto ${tokens.spacer.x5} auto;
`;

const StyledLoader = styled(Loader)`
  margin: ${tokens.spacer.x8} auto;
`;

export const ResultsControls = ({
  totalAggregatedResults,
  surveyId,
}: ResultsControlsProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('feedback:results.results-control');

  const selectedQuestion = useSelectedQuestion(surveyId, false);

  const isOpenEndedType = useMemo(
    () =>
      selectedQuestion !== 'ALL' &&
      (selectedQuestion?.type === QuestionTypes.SINGLE_OPEN_ENDED_SINGLE_LINE ||
        selectedQuestion?.type === QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE),
    [selectedQuestion],
  );
  const [hideAlert, setHideAlert] = useLocalStorageJSONValue<boolean>('hideWordCloudAlert');
  const showAlert = useMemo(
    () =>
      hideAlert !== true &&
      isOpenEndedType &&
      totalAggregatedResults !== undefined &&
      totalAggregatedResults > 0,
    [hideAlert, isOpenEndedType, totalAggregatedResults],
  );
  const { data: site } = useCurrentSite();
  const editForwarding = useCallback(() => {
    if (!site) return;
    navigateTo(SURVEYS.EDIT.path, {
      surveyId,
      activeFlowSection: 'forwarding',
      siteId: site.id,
    });
  }, [site, surveyId]);

  const { beginExport, fileTooLargeModal } = useExportResponses(surveyId);

  if (selectedQuestion === undefined) return <StyledLoader size="l" />;

  return (
    <StyledContainer>
      <QuestionContainer>
        <ResultsQuestionSelector
          surveyId={surveyId}
          trackingEventName="Results view question selected"
          allowAll={false}
        />
        <ActionsWrapper>
          <ExportResults csv xlsx onExportSelected={beginExport} />
          <ForwardSurveyResults
            surveyId={surveyId}
            onEdit={editForwarding}
            trackingUiPosition="Survey Results List"
            trackingState="Survey Results"
          />
        </ActionsWrapper>
      </QuestionContainer>
      {showAlert && (
        <StyledAlert
          kind="info"
          title={t('alert-wordcloud-title')}
          isDismissible
          onDismiss={() => setHideAlert(true)}
        >
          {t('alert-wordcloud-text')}
        </StyledAlert>
      )}
      {fileTooLargeModal}
    </StyledContainer>
  );
};
